<div class="teams-page">
  <app-workspace-hub-nav></app-workspace-hub-nav>
  <section class="teams-hero">
    <div>
      <div class="teams-kicker">Administration</div>
      <h1>Workspace</h1>
      <p>Manage service permissions, scanning, monitoring rules, and capacity for your enterprise workspace.</p>
    </div>
    <div class="teams-actions"><a routerLink="/teams/members" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg shadow hover:bg-blue-100 border-0 cursor-pointer transition-colors no-underline">Manage members</a></div>
  </section>

  @if (contextState === 'loading') {
    <section class="teams-empty">Loading&hellip;</section>
  }

  @if (contextState === 'noStorage') {
    <section class="teams-empty teams-empty-warning">
      Your workspace is not yet provisioned with storage. Please contact support to complete setup.
    </section>
  }

  @if (contextState === 'noLicense') {
    <section class="teams-empty teams-empty-error">
      No FlowStudio for Teams licenses found.
    </section>
  }

  @if (contextState === 'ready') {
    <section class="teams-two-col">
      <article class="teams-card">
        <div class="teams-section-title" style="justify-content:space-between">
          <div class="inline-flex items-center gap-1">
            <img class="teams-title-icon" src="/assets/icons/data_usage_20_regular.svg" alt="" aria-hidden="true" />
            <h2>Scanning Status</h2>
          </div>
          <button type="button" class="inline-flex items-center justify-center w-7 h-7 text-slate-500 bg-white rounded-md shadow hover:bg-slate-50 border-0 cursor-pointer transition-colors" title="Refresh All" (click)="refreshStats()">&#8635;</button>
        </div>
        @if ((scans$ | async); as scans) {
          <div class="teams-scan-list">
            @for (scan of scans; track scan; let i = $index) {
              <div class="teams-scan">
                <div class="teams-scan-topline">
                  <div class="teams-scan-name">
                    Power Platform Scan
                    @if (this.findAccount(scan.account); as account) {
                      <span class="teams-scan-account">[{{ account.unique_name }}]</span>
                    }
                  </div>
                  <div class="teams-scan-status" [class.is-active]="!scan?.end" [class.is-done]="scan?.end">
                    {{ scan?.end ? 'Complete' : 'In Progress' }}
                  </div>
                </div>
                @if (((scansTimespan$ | async) || [])[i]; as t) {
                  <div class="teams-scan-detail">
                    {{ t.duration }}
                    @if (t.when) {
                      <span class="teams-scan-when">{{ t.inProgress ? ' started ' : ' on ' }}{{ t.when }}</span>
                    }
                  </div>
                } @else {
                  <div class="teams-scan-detail">…</div>
                }
                <div class="teams-scan-metrics">
                  <span>{{ scan?.envs || 0 }} environments</span>
                  <span>{{ scan?.apps || 0 }} apps</span>
                  <span>{{ scan?.flows || 0 }} flows</span>
                </div>
              </div>
            }
            @if (scans.length === 0) {
              <div class="teams-scan-empty">
                To start scanning, add permissions for Power Automate and Power Apps.
              </div>
            }
          </div>
        } @else {
          <div class="teams-scan-empty">To start scanning, add permissions for Power Automate and Power Apps.</div>
        }
      </article>
      <article class="teams-card">
        <div class="teams-section-title" style="justify-content:space-between">
          <div class="inline-flex items-center gap-1">
            <img class="teams-title-icon" src="/assets/icons/branch_fork_link_20_regular.svg" alt="" aria-hidden="true" />
            <h2>Monitoring Rules</h2>
          </div>
          <button type="button" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-amber-50 rounded-lg shadow hover:bg-amber-100 border border-amber-300 cursor-pointer transition-colors" title="Webhook delivery is not yet active" (click)="schedule.addRule($event)">Add New Rule</button>
        </div>
        <app-schedule #schedule [rules$]="rules$"></app-schedule>
      </article>
    </section>
    <section class="teams-two-col teams-two-col-tight">
      <article class="teams-card">
        <div class="teams-section-title" style="justify-content:space-between">
          <div class="inline-flex items-center gap-1">
            <img class="teams-title-icon" src="/assets/icons/people_team_20_regular.svg" alt="" aria-hidden="true" />
            <h2>Permissions</h2>
          </div>
          @if ((accounts$ | async)?.length > 0) {
            <a
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white rounded-lg shadow hover:bg-slate-50 border-0 cursor-pointer transition-colors no-underline"
              [href]="'/api/auth?domain_hint=' + ((user$ | async)?.tid || '') + '&login_hint=' + ((user$ | async)?.username || '') + '&s=' + permComp.encodeStateParam('multi')">
              Add Power Automate &amp; Power Apps
            </a>
          }
        </div>
        <app-permission #permComp [accounts$]="this.accounts$" [user$]="this.user$" [workspace]="(this.license$|async)?.workspace"></app-permission>
      </article>
      <article class="teams-card">
        <div class="teams-section-title">
          <img class="teams-title-icon" src="/assets/icons/data_usage_checkmark_20_regular.svg" alt="" aria-hidden="true" />
          <h2>License & Capacity</h2>
        </div>
        <div class="teams-metric-list">
          <div>
            <span>Team Role</span>
            <strong>{{ (license$ | async)?.user_type || 'Team Owner' }}</strong>
          </div>
          <div>
            <span>Teams licence assigned</span>
            <strong>{{ (license$ | async)?.license_teams_assigned || 'Unavailable' }}</strong>
          </div>
          <div>
            <span>Workspace</span>
            <strong>{{ workspaceInfo || 'N/A' }}</strong>
          </div>
          <div>
            <span>Monitored flow limit</span>
            <strong>{{ workspaceFlowCapacity() === null ? 'Unavailable' : (workspaceFlowCapacity() | number) }}</strong>
          </div>
          <div>
            <span>Connected Accounts</span>
            <strong>{{ getAccountCount() | number }}</strong>
          </div>
          <div>
            <span>BYO Storage Key</span>
            <strong><a href="mailto:support@flowstudio.app" style="color:inherit;">Contact us</a></strong>
          </div>
        </div>
      </article>
    </section>
  }
</div>
